<template>
  <div class="min-h-screen flex flex-col bg-white text-fg font-sans">
    <!-- Header -->
    <header
      data-testid="header"
      class="fixed top-0 left-0 right-0 z-40 pt-safe backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300 bg-white/85 border-line/60 shadow-sm"
    >
      <div class="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <router-link
          data-testid="link-logo"
          class="flex items-center gap-2 cursor-pointer"
          to="/"
        >
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </router-link>

        <nav class="hidden md:flex items-center gap-6">
          <button
            data-testid="nav-서비스"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('services')"
          >
            서비스
          </button>
          <button
            data-testid="nav-혜택"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('benefits')"
          >
            혜택
          </button>
          <button
            data-testid="nav-성과"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('performance')"
          >
            성과
          </button>
          <button
            data-testid="nav-진행 방식"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('process')"
          >
            진행 방식
          </button>
          <button
            data-testid="nav-FAQ"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('faq')"
          >
            FAQ
          </button>
        </nav>

        <div class="flex items-center gap-3">
          <button
            data-testid="button-header-cta"
            class="hidden md:inline-flex items-center px-5 py-2.5 rounded-[12px] border text-sm font-semibold backdrop-blur-sm transition-colors cursor-pointer border-line bg-white/60 text-fg hover:bg-white"
            @click="openModal"
          >
            무료 상담 신청
          </button>
          <button
            data-testid="button-mobile-menu"
            class="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px] transition-colors cursor-pointer text-fg"
            aria-label="메뉴 열기"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu"
              aria-hidden="true"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Drawer -->
      <div v-if="isMobileMenuOpen" class="md:hidden bg-white border-b border-line px-4 py-4 space-y-3 shadow-md">
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('services')">서비스</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('benefits')">혜택</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('performance')">성과</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('process')">진행 방식</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('faq')">FAQ</button>
        <button data-testid="button-header-cta" class="w-full py-3 rounded-xl bg-brand-500 text-white font-bold text-sm" @click="openModal">무료 상담 신청</button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <div class="pb-24 md:pb-0">
        <!-- Hero Section -->
        <section class="py-16 md:py-24 bg-gradient-to-br" :class="data.heroBg">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <router-link
              :data-testid="'link-back-' + data.id"
              class="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg mb-8 transition-colors"
              to="/"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left" aria-hidden="true">
                <path d="m12 19-7-7 7-7" />
                <path d="M19 12H5" />
              </svg>
              전체 서비스
            </router-link>

            <span class="text-xs font-bold uppercase tracking-widest text-brand-500 mb-3 block">
              SERVICE {{ data.num }} / {{ data.badgeName }}
            </span>

            <h1 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-5 whitespace-pre-line">
              {{ data.title }}
            </h1>

            <p class="text-base text-fg-muted leading-relaxed mb-6 max-w-2xl">
              {{ data.desc }}
            </p>

            <div class="flex flex-wrap gap-2 mb-8">
              <span
                v-for="(tag, idx) in data.tags"
                :key="idx"
                class="px-3 py-1.5 rounded-full text-xs font-semibold"
                :class="data.tagStyle"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </section>

        <!-- Problem Section -->
        <section class="py-16 bg-bg-soft">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <p class="text-xs font-semibold uppercase tracking-widest text-brand-500 mb-3 text-center">이런 상황이라면</p>
            <h2 class="text-xl md:text-2xl font-bold text-fg mb-8 text-center">{{ data.badgeName }} 체험단이 필요합니다</h2>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div
                v-for="(prob, idx) in data.problems"
                :key="idx"
                :data-testid="data.id + '-problem-' + idx"
                class="bg-white rounded-[16px] p-6 border border-line"
              >
                <h3 class="text-base font-semibold text-fg mb-3">{{ prob.title }}</h3>
                <p class="text-sm text-fg-muted leading-relaxed">{{ prob.desc }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Scope Section -->
        <section class="py-16 bg-white">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <p class="text-xs font-semibold uppercase tracking-widest text-brand-500 mb-3">제공 범위</p>
            <h2 class="text-xl md:text-2xl font-bold text-fg mb-8">티블이 운영하고, 사장님은 승인만</h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-brand-50 rounded-[16px] p-6 border border-brand-100">
                <p class="text-xs font-bold uppercase tracking-widest text-brand-500 mb-4">티블이 운영합니다</p>
                <ul class="space-y-2.5">
                  <li v-for="(item, idx) in data.tbleScope" :key="idx" class="text-sm text-fg-muted flex items-start gap-2">
                    <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0"></span>
                    {{ item }}
                  </li>
                </ul>
              </div>

              <div class="bg-bg-soft rounded-[16px] p-6 border border-line">
                <p class="text-xs font-bold uppercase tracking-widest text-fg-subtle mb-4">광고주가 준비합니다</p>
                <ul class="space-y-2.5">
                  <li v-for="(item, idx) in data.ownerScope" :key="idx" class="text-sm text-fg-muted flex items-start gap-2">
                    <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <!-- Flow Section -->
        <section class="py-16 bg-bg-soft">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <p class="text-xs font-semibold uppercase tracking-widest text-brand-500 mb-3">운영 흐름</p>
            <h2 class="text-xl md:text-2xl font-bold text-fg mb-8">단계별로 투명하게 진행됩니다</h2>

            <div class="rounded-[20px] border border-line bg-white p-6 md:p-8">
              <!-- Desktop Step Flow -->
              <ol class="hidden md:flex items-start">
                <li
                  v-for="(st, idx) in data.steps"
                  :key="idx"
                  class="relative flex flex-1 flex-col items-center text-center"
                >
                  <span
                    v-if="idx < data.steps.length - 1"
                    class="absolute left-1/2 top-5 h-0.5 w-full -translate-y-1/2 bg-gradient-to-r from-brand-400 to-brand-200"
                  ></span>
                  <span class="relative z-10 grid h-10 w-10 place-items-center rounded-full bg-brand-500 text-white text-sm font-bold ring-4 ring-white shadow-sm">
                    {{ idx + 1 }}
                  </span>
                  <p class="mt-3 px-1 text-sm font-semibold text-fg leading-snug">{{ st }}</p>
                  <span class="mt-1 text-[11px] font-semibold tracking-wider text-fg-subtle">
                    STEP 0{{ idx + 1 }}
                  </span>
                </li>
              </ol>

              <!-- Mobile Step Flow -->
              <ol class="md:hidden">
                <li
                  v-for="(st, idx) in data.steps"
                  :key="idx"
                  class="relative flex items-start gap-3.5"
                  :class="idx < data.steps.length - 1 ? 'pb-6' : ''"
                >
                  <span
                    v-if="idx < data.steps.length - 1"
                    class="absolute left-[1.125rem] top-10 bottom-0 w-0.5 -translate-x-1/2 bg-gradient-to-b from-brand-300 to-brand-200"
                  ></span>
                  <span class="relative z-10 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-brand-500 text-white text-sm font-bold ring-4 ring-white shadow-sm">
                    {{ idx + 1 }}
                  </span>
                  <div class="pt-1">
                    <span class="block text-[11px] font-semibold tracking-wider text-fg-subtle">STEP 0{{ idx + 1 }}</span>
                    <p class="text-sm font-semibold text-fg leading-snug">{{ st }}</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <!-- Effects Section -->
        <section class="py-16 bg-white">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <p class="text-xs font-semibold uppercase tracking-widest text-brand-500 mb-3 text-center">기대 효과</p>
            <h2 class="text-xl md:text-2xl font-bold text-fg mb-8 text-center">운영 후 달라지는 것들</h2>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div
                v-for="(eff, idx) in data.effects"
                :key="idx"
                class="bg-bg-soft border border-line rounded-[16px] p-6 text-center"
              >
                <p class="text-base font-bold mb-2" :class="data.accentColor">{{ eff.title }}</p>
                <p class="text-sm text-fg-muted">{{ eff.desc }}</p>
              </div>
            </div>

            <div class="mb-10">
              <div class="mb-5 text-center">
                <span class="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-3.5 py-1.5 text-xs font-bold tracking-wide text-brand-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-gift" aria-hidden="true">
                    <rect x="3" y="8" width="18" height="4" rx="1" />
                    <path d="M12 8v13" />
                    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
                    <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
                  </svg>
                  관련 혜택
                </span>
              </div>

              <div class="mx-auto grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
                <div
                  v-for="(ben, idx) in data.benefits"
                  :key="idx"
                  class="flex items-start gap-3 rounded-[16px] border border-brand-200 bg-brand-50 p-5 shadow-sm"
                >
                  <span class="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-brand-500 text-white shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <div>
                    <p class="text-sm font-bold text-fg leading-snug">{{ ben.title }}</p>
                    <p class="mt-1 text-xs leading-relaxed text-fg-muted">{{ ben.desc }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center pt-6">
              <button
                type="button"
                data-testid="button-services-cta"
                class="px-8 py-4 rounded-[12px] bg-brand-500 text-white font-semibold text-lg hover:bg-brand-600 transition-colors cursor-pointer"
                @click="openModal"
              >
                지금 무료 상담 신청하기
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-fg text-white/70 pt-12 pb-28 md:pb-12">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <div class="mb-8 flex items-center gap-3">
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </div>
        <p class="text-sm text-white/50 mb-8 -mt-4">다채널 리뷰 마케팅으로 매출을 올리는 광고주 서비스</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs leading-relaxed mb-8 text-white/50">
          <div class="space-y-1">
            <p>상호명: 주식회사 쓰리얼라이언스</p>
            <p>대표이사: 성동효</p>
            <p>주소: 서울시 강남구 학동로 44길 16, 1F</p>
            <p>사업자등록번호: 217-81-47577</p>
            <p>통신판매등록번호: 제2025-서울강남-04400호</p>
          </div>
          <div class="space-y-1">
            <p>개인정보 보호 최고책임자: 성동효</p>
            <p>이메일: help@3alliance.co.kr</p>
            <!-- <p>대표전화: 1833-5985</p> -->
            <!-- <p>FAX: 0504-334-6358</p> -->
            <p>상담시간: 10:00 ~ 18:00 · 토·일·공휴일 휴무</p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-1 md:gap-4">
          <p class="text-xs text-white/40 py-2">© 2026 주식회사 쓰리얼라이언스. All rights reserved.</p>
          <div class="flex gap-4 -my-1">
            <a href="#" data-testid="footer-link-개인정보 처리방침" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>개인정보 처리방침</a>
            <a href="#" data-testid="footer-link-이용약관" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>이용약관</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Floating Contact Button -->
    <button
      type="button"
      data-testid="button-floating-contact"
      aria-label="무료 상담 신청"
      class="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-brand-500 text-white font-semibold text-sm shadow-[0_8px_24px_rgba(242,178,42,0.45)] hover:bg-brand-600 hover:shadow-[0_12px_28px_rgba(242,178,42,0.55)] transition-all hover:scale-105 cursor-pointer"
      @click="toggleModal"
    >
      <span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
        <img src="/images/headset.svg" alt="" class="w-4 h-4 brightness-0 invert" />
      </span>
      <span>문의하기</span>
    </button>

    <!-- Consultation Widget Modal -->
    <ConsultationModal :is-open="isModalOpen" @close="isModalOpen = false" />
  </div>
</template>

<script setup>
/* global defineProps */
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import ConsultationModal from '../common/ConsultationModal.vue';

const props = defineProps({
  serviceId: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const isMobileMenuOpen = ref(false);
const isModalOpen = ref(false);

const serviceDetails = {
  blog: {
    id: 'blog',
    num: '01',
    badgeName: '네이버 블로그',
    heroBg: 'from-brand-50 to-white',
    title: '검색했을 때 먼저 보이는 가게,\n블로그 리뷰로 만듭니다',
    desc: '검증된 리뷰어가 직접 써 내려간 블로그 리뷰로, 네이버 검색과 플레이스에서 손님이 우리를 먼저 만나게 합니다.',
    tags: ['지역 매장·프랜차이즈 지점', '검색 유입이 매출에 영향 큰 제품', '네이버 검색 노출이 약한 브랜드'],
    tagStyle: 'bg-brand-100 text-brand-700',
    accentColor: 'text-brand-500',
    problems: [
      { title: '검색해도 우리 콘텐츠가 안 보입니다', desc: '광고를 끄면 검색에서 같이 사라집니다. 블로그 글이 쌓여 있어야 광고비 안 들이고도 들어오는 손님이 생깁니다.' },
      { title: '경쟁사 블로그 리뷰가 압도적으로 많습니다', desc: '손님이 비교 검색을 시작하는 순간, 글이 많고 최근일수록 더 믿음이 갑니다.' },
      { title: '광고 외 신뢰 콘텐츠가 필요합니다', desc: '직접 가보고 써 본 블로그 글 한 편이 광고 문구 한 줄보다 마지막 결정을 더 크게 좌우합니다.' },
    ],
    tbleScope: [
      '업종/상품 키워드 가이드 작성',
      '영향력 기준 리뷰어 모집·선정',
      '콘텐츠 미션·가이드 전달',
      '등록 리뷰 8가지 항목 검수 (키워드·링크·이미지·텍스트 분량 등)',
      '누적 성과 보고',
    ],
    ownerScope: [
      '매장 방문/제품 제공 기준 안내',
      '강조하고 싶은 키워드·소구점',
      '진행 승인',
    ],
    steps: ['신청', '기획·키워드 가이드', '리뷰어 모집·선정', '방문/제품 사용', '리뷰 등록', '품질 검수', '누적 보고'],
    effects: [
      { title: '노출', desc: '네이버 검색 결과·플레이스 안에 우리 브랜드 콘텐츠 누적' },
      { title: '신뢰', desc: '직접 써 본 상세 리뷰가 쌓여 광고가 못 주는 신뢰를 만듭니다' },
      { title: '전환', desc: '비교하는 단계에서 한발 앞서 방문·문의로 이어집니다' },
    ],
    benefits: [
      { title: '블로그기자단 지원', desc: '제품/식사 제공 없이 원고료 100% 지원' },
      { title: '검색노출 최적화', desc: '플레이스·키워드 함께 점검' },
    ],
  },
  instagram: {
    id: 'instagram',
    num: '02',
    badgeName: '인스타그램·릴스',
    heroBg: 'from-pink-50 to-white',
    title: '써 보는 장면 하나가\n다음 손님을 데려옵니다',
    desc: '검증된 크리에이터의 피드·릴스·숏폼으로 우리 브랜드의 분위기와 써 보는 모습을, 아직 모르는 손님 앞에 빠르게 퍼뜨립니다.',
    tags: ['시각적 비주얼이 중요한 카페·뷰티·숙박', 'MZ 타깃 라이프스타일 제품', '신상품 인지가 필요한 브랜드'],
    tagStyle: 'bg-pink-100 text-pink-700',
    accentColor: 'text-pink-600',
    problems: [
      { title: '우리 인스타가 비어 있습니다', desc: '새 손님은 검색보다 피드에서 먼저 마주칩니다. 피드가 비어 있으면 알지도, 믿지도 못합니다.' },
      { title: '숏폼 콘텐츠 만들 여력이 없습니다', desc: '지금 인스타는 릴스를 먼저 밀어줍니다. 직접 찍자니 손이 많이 가고, 그렇다고 결과가 나온다는 보장도 없습니다.' },
      { title: '신상품을 신상품답게 보여줘야 합니다', desc: '광고만 봐서는 어떻게 쓰는지 그려지지 않습니다. 실제로 써 본 영상과 사진이 구매를 결정짓습니다.' },
    ],
    tbleScope: [
      '업종/소구점 기반 인플루언서 풀 매칭',
      '콘텐츠 미션 가이드 (해시태그·태깅·강조점)',
      '피드·릴스·스토리 진행 관리',
      '등록 콘텐츠 검수 (스폰서 배너·키워드·링크 포함 여부)',
      '누적 노출·좋아요·도달 보고',
    ],
    ownerScope: [
      '제품 제공 또는 매장 방문 조건',
      '브랜드 가이드(로고·금지 표현)',
      '진행 승인',
    ],
    steps: ['신청', '인플루언서 풀 매칭', '콘텐츠 가이드 전달', '제작·발행', '검수', '누적 보고'],
    effects: [
      { title: '확산', desc: '피드·릴스·숏폼을 함께 돌려 SNS에서 더 멀리 닿습니다' },
      { title: '인지', desc: '써 보는 장면과 분위기로 손님 머릿속에 브랜드를 남깁니다' },
      { title: '관심', desc: '저장·공유·DM처럼 직접 움직이게 만듭니다' },
    ],
    benefits: [
      { title: '숏폼영상 무료', desc: '이달 신규 계약 광고주 대상 제작비 0원' },
      { title: '리뷰 무제한', desc: '캠페인당 인원 제한 없이 운영' },
    ],
  },
  'purchase-review': {
    id: 'purchase-review',
    num: '03',
    badgeName: '구매평 후기',
    heroBg: 'from-amber-50 to-white',
    title: '장바구니 앞에서 망설이지 않게\n구매평 자산을 쌓아드립니다',
    desc: '스마트스토어와 자사몰에 텍스트·포토·동영상 구매평을 쌓아 이탈하던 고객의 구매 결정을 이끌어냅니다.',
    tags: ['스마트스토어·자사몰 운영자', '상세 전환율 개선이 필요한 상품', '가격 비교가 치열한 카테고리'],
    tagStyle: 'bg-amber-100 text-amber-700',
    accentColor: 'text-amber-600',
    problems: [
      { title: '구매평이 부족해 결제를 주저합니다', desc: '유입은 많지만 실제 구매평이 없거나 부족하면 장바구니에서 손님이 돌아섭니다.' },
      { title: '텍스트 리뷰만 있고 포토·동영상이 부족합니다', desc: '생생한 포토 및 실사용 동영상 후기가 있어야 구매 신뢰도가 극대화됩니다.' },
      { title: '경쟁 제품보다 후기 수에서 밀립니다', desc: '동일 카테고리 비교 시 후기 수와 평점이 우수한 스토어로 결제가 쏠립니다.' },
    ],
    tbleScope: [
      '실구매 기반 검증된 체험단 매칭',
      '포토·동영상·텍스트 맞춤 가이드',
      '구매평 작성 및 등록 상태 확인',
      '품질 검수 (가이드 준수 여부)',
      '스토어 평점 및 전환 모니터링',
    ],
    ownerScope: [
      '체험 상품 발송 조건 안내',
      '강조 소구점 전달',
      '진행 승인',
    ],
    steps: ['신청', '체험단 매칭', '구매 및 제품 배송', '포토·동영상 리뷰 작성', '품질 검수', '누적 보고'],
    effects: [
      { title: '전환', desc: '구매 직전 망설이던 고객을 장바구니에서 결제로 연결' },
      { title: '자산', desc: '텍스트·포토·동영상 종합 리뷰 자산 축적' },
      { title: '평점', desc: '스토어 종합 평점 및 신뢰도 대폭 강화' },
    ],
    benefits: [
      { title: '리뷰 무제한', desc: '캠페인당 인원 제한 없이 운영' },
      { title: '검색노출 최적화', desc: '스토어 키워드 함께 점검' },
    ],
  },
  'map-review': {
    id: 'map-review',
    num: '04',
    badgeName: '맵리뷰',
    heroBg: 'from-emerald-50 to-white',
    title: '지도 보고 검색하는 손님을\n우리 매장으로 데려옵니다',
    desc: '네이버 영수증 리뷰와 구글·다음 지도 리뷰를 구축하여 지역 검색 시 상위 노출과 실제 매장 방문율을 끌어올립니다.',
    tags: ['식당·카페·뷰티·병원·학원', '지도·플레이스 리뷰가 적은 매장', '지역 키워드 검색이 핵심인 업종'],
    tagStyle: 'bg-emerald-100 text-emerald-700',
    accentColor: 'text-emerald-600',
    problems: [
      { title: '지도에서 매장을 찾아도 리뷰가 몇 개 없습니다', desc: '손님이 근처 맛집이나 매장을 지도 앱으로 찾을 때 리뷰 수가 적으면 방문 후보에서 빠집니다.' },
      { title: '영수증 인증 리뷰 수집이 어렵습니다', desc: '직접 손님에게 리뷰 요청을 하기가 번거롭고 참여율이 낮아 유지가 어렵습니다.' },
      { title: '지역 경쟁 매장보다 지도 순위가 낮습니다', desc: '플레이스 순위 상승에는 실 방문객 기반 영수증 리뷰와 포토 등록이 필수적입니다.' },
    ],
    tbleScope: [
      '영수증/방문 인증 체험단 매칭',
      '지역 키워드 및 지도 가이드 작성',
      '포토 영수증 리뷰 등록 관리',
      '플레이스 노출 환경 점검',
      '누적 리뷰 및 지도 순위 보고',
    ],
    ownerScope: [
      '매장 방문 조건/메뉴 제공 안내',
      '기본 매장 정보 제공',
      '진행 승인',
    ],
    steps: ['신청', '방문 체험단 매칭', '매장 방문 및 이용', '영수증/지도 리뷰 등록', '품질 검수', '누적 보고'],
    effects: [
      { title: '방문', desc: '지도 검색 유저의 실제 오프라인 방문 유도' },
      { title: '순위', desc: '네이버 플레이스 및 지도 검색 순위 개선' },
      { title: '인증', desc: '방문 인증 기반의 높은 신뢰도 확보' },
    ],
    benefits: [
      { title: '검색노출 최적화', desc: '플레이스·지역 키워드 함께 점검' },
      { title: '리뷰 무제한', desc: '캠페인당 인원 제한 없이 운영' },
    ],
  },
  'app-review': {
    id: 'app-review',
    num: '05',
    badgeName: '앱 리뷰',
    heroBg: 'from-blue-50 to-white',
    title: '설치 직전 망설임을 없애는\n앱 마켓 평점·후기 구축',
    desc: 'App Store와 Google Play 마켓 평점 및 실사용자 후기를 보강하여 앱 다운로드 전환율을 극대화합니다.',
    tags: ['신규 출시 앱, 평점 개선이 필요한 앱', '설치 전 신뢰 확보가 필요한 서비스', '글로벌·국내 마켓 동시 운영 앱'],
    tagStyle: 'bg-blue-100 text-blue-700',
    accentColor: 'text-blue-600',
    problems: [
      { title: '마켓 평점이 낮아 앱 설치율이 안 나옵니다', desc: '유저가 앱을 다운로드하기 직전 평점과 후기를 확인하며 부정적 평가가 있으면 이탈합니다.' },
      { title: '신규 앱이라 리뷰와 평점 수가 절대 부족합니다', desc: '마켓에 등록된 리뷰 수가 적으면 유저가 앱의 안정성에 의구심을 가집니다.' },
      { title: '실사용 기반의 구체적 후기가 필요합니다', desc: '단순 평점이 아니라 앱의 주요 기능과 장점을 설명해 주는 후기가 설치를 설득합니다.' },
    ],
    tbleScope: [
      '실사용 기반 앱 리뷰어 매칭',
      '마켓 가이드라인 준수 리뷰 가이드 작성',
      'App Store / Google Play 리뷰 등록 관리',
      '평점 및 텍스트 품질 검수',
      '마켓 평점 및 다운로드 성과 보고',
    ],
    ownerScope: [
      '앱 링크 및 테스트 계정/환경 안내',
      '강조하고 싶은 주요 기능 전달',
      '진행 승인',
    ],
    steps: ['신청', '리뷰어 설치 및 체험', '마켓 평가/리뷰 등록', '품질 검수', '평점 모니터링', '누적 보고'],
    effects: [
      { title: '설치', desc: '다운로드 버튼을 누르도록 이탈 방지 및 설치 전환 보조' },
      { title: '평점', desc: 'App Store & Google Play 평점 대폭 개선' },
      { title: '신뢰', desc: '실사용자 텍스트 후기로 브랜드 신뢰도 구축' },
    ],
    benefits: [
      { title: '리뷰 무제한', desc: '캠페인당 인원 제한 없이 운영' },
      { title: '검색노출 최적화', desc: '마켓 키워드 함께 점검' },
    ],
  },
};

const data = computed(() => serviceDetails[props.serviceId] || serviceDetails.blog);

const navToHomeSection = (sectionId) => {
  isMobileMenuOpen.value = false;
  router.push({ path: '/', hash: '#' + sectionId });
};

const openModal = () => {
  isModalOpen.value = true;
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};
</script>

<style scoped>
input::placeholder,
textarea::placeholder {
  color: #6b7280 !important;
  opacity: 1 !important;
}
</style>
